import React, { useState, useEffect, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  Table,
  Form,
  Pagination,
  Dropdown,
  ButtonGroup,
} from 'react-bootstrap';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

// Draggable Column Header Component
const DraggableColumnHeader = ({ header, table }) => {
  const { column } = header;
  const [, ref] = useDrag({
    type: 'column',
    item: () => column,
  });

  const [{ isOver }, drop] = useDrop({
    accept: 'column',
    drop: (draggedColumn) => {
      if (draggedColumn.id === column.id) return;
      
      const newColumnOrder = [...table.getState().columnOrder];
      const draggedIndex = newColumnOrder.indexOf(draggedColumn.id);
      const targetIndex = newColumnOrder.indexOf(column.id);
      
      newColumnOrder.splice(draggedIndex, 1);
      newColumnOrder.splice(targetIndex, 0, draggedColumn.id);
      
      table.setColumnOrder(newColumnOrder);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  return (
    <th
      ref={(node) => ref(drop(node))}
      key={header.id}
      role="button"
      onClick={header.column.getToggleSortingHandler()}
      className={`${header.column.columnDef.meta?.sticky ? 'sticky-column' : ''} ${
        isOver ? 'drop-target' : ''
      }`}
      style={{ 
        cursor: 'move',
        backgroundColor: isOver ? '#f0f0f0' : undefined,
      }}
    >
      {flexRender(header.column.columnDef.header, header.getContext())}
      {header.column.getIsSorted() === 'asc' ? ' 🔼' : ''}
      {header.column.getIsSorted() === 'desc' ? ' 🔽' : ''}
    </th>
  );
};

const DataTable = ({
  columns = [],
  data = [],
  onRowClick = () => {},
  filters = [],
  filterKey = '',
  filterFn = null,
  tableId = 'default'
}) => {
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('');
  const STORAGE_KEY = `datatable_column_visibility_${tableId}`;
  const COLUMN_ORDER_KEY = `datatable_column_order_${tableId}`;

  // Apply filter internally
  const filteredData = useMemo(() => {
    if (!selectedFilter) return data;

    if (typeof filterFn === 'function') {
      return data.filter(item => filterFn(item, selectedFilter));
    }

    if (filterKey) {
      return data.filter(item => String(item[filterKey]) === String(selectedFilter));
    }

    return data;
  }, [data, selectedFilter, filterKey, filterFn]);

  const [columnVisibility, setColumnVisibility] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const [columnOrder, setColumnOrder] = useState(() => {
    try {
      const saved = localStorage.getItem(COLUMN_ORDER_KEY);
      if (saved) {
        // Filter out any columns that don't exist in the current columns prop
        const savedOrder = JSON.parse(saved);
        const validColumns = columns.map(col => col.accessorKey || col.id);
        return savedOrder.filter(colId => validColumns.includes(colId));
      }
      // Default to the order of columns as passed in props
      return columns.map(col => col.accessorKey || col.id);
    } catch {
      // Default to the order of columns as passed in props
      return columns.map(col => col.accessorKey || col.id);
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(columnVisibility));
    localStorage.setItem(COLUMN_ORDER_KEY, JSON.stringify(columnOrder));
  }, [columnVisibility, columnOrder]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      globalFilter,
      sorting,
      columnVisibility,
      columnOrder,
    },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const pagination = table.getState().pagination;

  const renderPagination = () => {
    const pageCount = table.getPageCount();
    const currentPage = pagination.pageIndex;
    if (pageCount <= 1) return null;

    const visiblePages = [];
    const addPage = (number) => {
      visiblePages.push(
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => table.setPageIndex(number)}
        >
          {number + 1}
        </Pagination.Item>
      );
    };

    const addEllipsis = (key) => {
      visiblePages.push(<Pagination.Ellipsis key={key} disabled />);
    };

    for (let i = 0; i < pageCount; i++) {
      if (
        i === 0 ||
        i === pageCount - 1 ||
        (i >= currentPage - 2 && i <= currentPage + 2)
      ) {
        addPage(i);
      } else if (i === 1 && currentPage > 3) {
        addEllipsis('start');
      } else if (i === pageCount - 2 && currentPage < pageCount - 4) {
        addEllipsis('end');
      } else {
        continue;
      }
    }

    return (
      <Pagination className="mb-0">
        <Pagination.First onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()} />
        <Pagination.Prev onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()} />
        {visiblePages}
        <Pagination.Next onClick={() => table.nextPage()} disabled={!table.getCanNextPage()} />
        <Pagination.Last onClick={() => table.setPageIndex(pageCount - 1)} disabled={!table.getCanNextPage()} />
      </Pagination>
    );
  };

  return (
    <>
      <style>
        {`
          .sticky-column {
            position: sticky;
            right: 0;
            background: white;
            z-index: 2;
            box-shadow: -2px 0 4px rgba(0, 0, 0, 0.05);
          }
          .draggable-header {
            cursor: move;
            user-select: none;
          }
          .drop-target {
            border-top: 2px solid #1890ff;
            transition: background-color 0.2s;
          }
        `}
      </style>

      <div className="d-flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
        {filters.length > 0 && (
          <Form.Select
            value={selectedFilter}
            onChange={e => {
              setSelectedFilter(e.target.value);
              table.setPageIndex(0);
            }}
            style={{ maxWidth: '200px' }}
          >
            {filters.map(({ label, value }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Form.Select>
        )}
          <div className="flex-grow-1 d-flex justify-content-center">
            <Form.Control
              type="text"
              placeholder="Search..."
              value={globalFilter}
              onChange={e => {
                setGlobalFilter(e.target.value);
                table.setPageIndex(0);
              }}
              style={{  width: '100%' }}
            />
          </div>

        <Dropdown as={ButtonGroup}>
          <Dropdown.Toggle variant="secondary" id="column-visibility-dropdown">
            Columns
          </Dropdown.Toggle>
          <Dropdown.Menu
            className="px-2"
            onClick={(e) => e.stopPropagation()}
            style={{ maxHeight: '300px', overflowY: 'auto' }}
          >
            {table.getAllLeafColumns().map(column => (
              <div
                key={column.id}
                onClick={() => column.toggleVisibility()}
                className="d-flex align-items-center gap-2 py-1 px-2 dropdown-item"
                style={{ cursor: 'pointer' }}
              >
                <Form.Check
                  type="checkbox"
                  checked={column.getIsVisible()}
                  readOnly
                  onClick={(e) => e.stopPropagation()}
                />
                <span>{column.columnDef.header}</span>
              </div>
            ))}
            <Dropdown.Divider />
            <Dropdown.Item onClick={() => {
              setColumnVisibility({});
              setColumnOrder(columns.map(col => col.accessorKey || col.id));
            }}>
              Reset Columns
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
      <div className="table-wrapper">
        <Table striped bordered hover responsive className="mb-0">
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <DraggableColumnHeader key={header.id} header={header} table={table} />
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => (
              <tr
                key={row.id}
                onClick={(e) => {
                  if (
                    e.target.closest('button') ||
                    e.target.closest('a') ||
                    e.target.closest('[role="button"]')
                  ) return;
                  onRowClick?.(row.original);
                }}
                style={{ cursor: onRowClick ? 'pointer' : 'default' }}
              >
                {row.getVisibleCells().map(cell => (
                  <td
                    key={cell.id}
                    className={cell.column.columnDef.meta?.sticky ? 'sticky-column' : ''}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="d-flex align-items-center gap-3">
          <Form.Select
            value={pagination.pageSize}
            onChange={e => {
              table.setPageSize(Number(e.target.value));
              table.setPageIndex(0);
            }}
            style={{ width: 'auto' }}
          >
            {[5, 10, 20, 50].map(size => (
              <option key={size} value={size}>
                Show {size}
              </option>
            ))}
          </Form.Select>

          <div className="text-muted small">
            Showing{' '}
            {table.getRowModel().rows.length > 0
              ? `${pagination.pageIndex * pagination.pageSize + 1}–${Math.min(
                  (pagination.pageIndex + 1) * pagination.pageSize,
                  table.getFilteredRowModel().rows.length
                )}`
              : '0'}{' '}
            of {table.getFilteredRowModel().rows.length}
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          {renderPagination()}
          <Form
            onSubmit={e => {
              e.preventDefault();
              const pageInput = e.target.elements.pageNumber.value;
              const page = Number(pageInput) - 1;

              if (!isNaN(page) && page >= 0 && page < table.getPageCount()) {
                table.setPageIndex(page);
              }
            }}
          >
            <Form.Control
              type="number"
              name="pageNumber"
              min={1}
              max={table.getPageCount()}
              placeholder="Go to page"
              style={{ width: '120px', marginLeft: '10px' }}
            />
          </Form>
        </div>
      </div>
    </>
  );
};

export default DataTable;